const num = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const divider = (value) => (num(value) > 0 ? num(value) : 1);

export const MATERIAL_KEYS = ['material1', 'material2', 'material3'];
export const FITTING_KEYS = ['fittings1', 'fittings2', 'fittings3', 'fittings4'];

export const CATALOG_TYPES = [
    { id: 'material', label: 'Материал', accent: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' },
    { id: 'cutting', label: 'Резка', accent: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' },
    { id: 'printing', label: 'Печать', accent: 'bg-cyan-100 text-cyan-700', dot: 'bg-cyan-500' },
    { id: 'fittings', label: 'Фурнитура', accent: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
    { id: 'commission', label: 'Комиссия', accent: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' },
    { id: 'processing', label: 'Обработка', accent: 'bg-teal-100 text-teal-700', dot: 'bg-teal-500' },
    { id: 'acquiring', label: 'Эквайринг', accent: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
    { id: 'tax', label: 'Налог', accent: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
    { id: 'employee', label: 'Сотрудник', accent: 'bg-fuchsia-100 text-fuchsia-700', dot: 'bg-fuchsia-500' },
    { id: 'premises', label: 'Помещение', accent: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
];

export const CATALOG_GROUPS = [
    {
        id: 'materials',
        label: 'Материалы',
        types: ['material', 'cutting', 'printing', 'fittings'],
    },
    {
        id: 'ozon',
        label: 'Ozon',
        types: ['commission', 'processing', 'acquiring'],
    },
    {
        id: 'other',
        label: 'Налог, сотрудники и помещение',
        types: ['tax', 'employee', 'premises'],
    },
];

export const groupedCatalogOptions = CATALOG_GROUPS.map((group) => ({
    label: group.label,
    options: group.types.map((id) => ({
        value: id,
        label: CATALOG_TYPES.find((item) => item.id === id)?.label || id,
    })),
}));

export const catalogTypeMap = Object.fromEntries(CATALOG_TYPES.map((item) => [item.id, item]));

export const emptyCostItem = (fragment = '', name = '') => ({
    fragment,
    name,
    price: 0,
    production: {
        material1: { item_id: '', divider: 1 },
        material2: { item_id: '', divider: 1 },
        material3: { item_id: '', divider: 1 },
        cutting: { item_id: '', divider: 1 },
        printing: { item_id: '', divider: 1 },
        fittings1: { item_id: '', divider: 1 },
        fittings2: { item_id: '', divider: 1 },
        fittings3: { item_id: '', divider: 1 },
        fittings4: { item_id: '', divider: 1 },
        premises_id: '',
    },
    ozon: {
        commission_id: '',
        commission_pct: 0,
        acquiring_id: '',
        acquiring_pct: 0,
        processing_id: '',
        processing: 0,
        logistics_min: 0,
        logistics_max: 0,
        last_mile: 0,
    },
    tax_id: '',
    tax_pct: 0,
    employee_id: '',
});

const catalogMap = (catalog = []) =>
    Object.fromEntries((catalog || []).map((item) => [item.id, item]));

export const catalogUnitCost = (item) => {
    if (!item) return 0;
    if (isMonthlyType(item.type)) return num(item.cost) / divider(item.monthly_qty);
    return num(item.cost);
};

export const catalogCost = (catalog, itemId) => catalogUnitCost(catalogMap(catalog)[itemId]);

export const catalogName = (catalog, itemId) => catalogMap(catalog)[itemId]?.name || '';

const byType = (catalog, type) =>
    (catalog || [])
        .filter((item) => item.type === type)
        .sort((a, b) => a.name.localeCompare(b.name, 'ru'));

export const isPercentType = (type) =>
    type === 'commission' || type === 'acquiring' || type === 'tax';

export const isMonthlyType = (type) => type === 'premises' || type === 'employee';

export const catalogFieldLabels = (type) => {
    if (type === 'commission') {
        return { name: 'Категория товара', cost: 'Комиссия', placeholder: 'Например: Хобби / Игрушки' };
    }
    if (type === 'processing') {
        return { name: 'Тип обработки', cost: 'Сумма', placeholder: 'Например: Стандарт' };
    }
    if (type === 'acquiring') {
        return { name: 'Название', cost: 'Процент', placeholder: 'Например: Ozon эквайринг' };
    }
    if (type === 'tax') {
        return { name: 'Название', cost: 'Процент', placeholder: 'Например: УСН 6%' };
    }
    if (type === 'premises') {
        return {
            name: 'Название',
            cost: 'Сумма в месяц',
            placeholder: 'Например: Аренда цеха',
            qty: 'Изделий в месяц для помещения',
            qtyHint: 'Аренда делится на это количество. У сотрудника — своя цифра.',
        };
    }
    if (type === 'employee') {
        return {
            name: 'Название',
            cost: 'Сумма в месяц',
            placeholder: 'Например: Швея',
            qty: 'Изделий в месяц для сотрудника',
            qtyHint: 'Зарплата делится на это количество. У помещения — своя цифра.',
        };
    }
    return { name: 'Название', cost: 'Стоимость', placeholder: 'Например: Оксфорд 600' };
};

export const formatCatalogCost = (type, cost) => {
    const value = num(cost).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    return isPercentType(type) ? `${value}%` : `${value} ₽`;
};

export const formatCatalogSummary = (item) => {
    const formatted = formatCatalogCost(item.type, catalogUnitCost(item));
    return isMonthlyType(item.type) ? `${formatted} / шт` : formatted;
};

export const catalogOptions = (catalog, type) =>
    byType(catalog, type).map((item) => ({
        value: item.id,
        label: `${item.name} · ${formatCatalogSummary(item)}`,
        item,
    }));

const refPayload = (part) => ({
    item_id: String(part?.item_id || ''),
    divider: divider(part?.divider),
});

export const toPayload = (item) => ({
    fragment: String(item.fragment || '').trim().toUpperCase(),
    name: String(item.name || '').trim(),
    price: num(item.price),
    production: {
        material1: refPayload(item.production?.material1),
        material2: refPayload(item.production?.material2),
        material3: refPayload(item.production?.material3),
        cutting: refPayload(item.production?.cutting),
        printing: refPayload(item.production?.printing),
        fittings1: refPayload(item.production?.fittings1),
        fittings2: refPayload(item.production?.fittings2),
        fittings3: refPayload(item.production?.fittings3),
        fittings4: refPayload(item.production?.fittings4),
        premises_id: String(item.production?.premises_id || ''),
    },
    ozon: {
        commission_id: String(item.ozon?.commission_id || ''),
        commission_pct: num(item.ozon?.commission_pct),
        acquiring_id: String(item.ozon?.acquiring_id || ''),
        acquiring_pct: num(item.ozon?.acquiring_pct),
        processing_id: String(item.ozon?.processing_id || ''),
        processing: num(item.ozon?.processing),
        logistics_min: num(item.ozon?.logistics_min),
        logistics_max: num(item.ozon?.logistics_max),
        last_mile: num(item.ozon?.last_mile),
    },
    tax_id: String(item.tax_id || ''),
    tax_pct: num(item.tax_pct),
    employee_id: String(item.employee_id || ''),
});

const resolvedCost = (part, catalog) => {
    if (part?.item_id) return catalogCost(catalog, part.item_id);
    return num(part?.cost);
};

const perUnit = (part, catalog) => resolvedCost(part, catalog) / divider(part?.divider);

// Минимальная прибыль считается при максимальной логистике, максимальная — при минимальной.
export const calculateCost = (item, catalog = []) => {
    const data = toPayload(item);
    const { production: p, ozon: o } = data;
    const price = data.price;
    const premises = p.premises_id
        ? catalogCost(catalog, p.premises_id)
        : num(item.production?.premises);
    const employee = data.employee_id
        ? catalogCost(catalog, data.employee_id)
        : num(item.employee_cost);

    const productionParts = {
        material1: perUnit(p.material1, catalog),
        material2: perUnit(p.material2, catalog),
        material3: perUnit(p.material3, catalog),
        cutting: perUnit(p.cutting, catalog),
        printing: perUnit(p.printing, catalog),
        fittings1: perUnit(p.fittings1, catalog),
        fittings2: perUnit(p.fittings2, catalog),
        fittings3: perUnit(p.fittings3, catalog),
        fittings4: perUnit(p.fittings4, catalog),
        premises,
    };
    const production = Object.values(productionParts).reduce((sum, value) => sum + value, 0);
    const fittings = FITTING_KEYS.reduce((sum, key) => sum + productionParts[key], 0);

    const commissionPct = o.commission_id ? catalogCost(catalog, o.commission_id) : o.commission_pct;
    const acquiringPct = o.acquiring_id ? catalogCost(catalog, o.acquiring_id) : o.acquiring_pct;
    const processing = o.processing_id ? catalogCost(catalog, o.processing_id) : o.processing;
    const taxPct = data.tax_id ? catalogCost(catalog, data.tax_id) : data.tax_pct;
    const commission = (price * commissionPct) / 100;
    const acquiring = (price * acquiringPct) / 100;
    const ozonFixed = commission + acquiring + processing + o.last_mile;

    const scenario = (logistics) => {
        const ozon = ozonFixed + logistics;
        const taxBase = price - ozon - production;
        const tax = (Math.max(taxBase, 0) * taxPct) / 100;
        const profit = taxBase - tax - employee;
        return {
            logistics,
            ozon,
            taxBase,
            tax,
            profit,
            margin: price > 0 ? (profit / price) * 100 : 0,
        };
    };

    const best = scenario(Math.min(o.logistics_min, o.logistics_max));
    const worst = scenario(Math.max(o.logistics_min, o.logistics_max));

    return {
        price,
        productionParts,
        production,
        fittings,
        commission,
        acquiring,
        employee,
        best,
        worst,
    };
};

export const matchCostItem = (costItems, offerId) => {
    const haystack = String(offerId || '').toUpperCase();
    if (!haystack) return null;
    let best = null;
    (costItems || []).forEach((item) => {
        const fragment = String(item.fragment || '').toUpperCase();
        if (!fragment || !haystack.includes(fragment)) return;
        if (!best || fragment.length > String(best.fragment || '').length) {
            best = item;
        }
    });
    return best;
};

const averageCalc = (calc) => ({
    profit: (calc.worst.profit + calc.best.profit) / 2,
    margin: (calc.worst.margin + calc.best.margin) / 2,
});

const moneyValue = (value) => {
    if (value && typeof value === 'object') return Number(value.value ?? value.amount);
    return Number(value);
};

export const promoProfitLoss = (product, costItems, catalog) => {
    const costItem = matchCostItem(costItems, product?.offer_id);
    if (!costItem) return null;
    const actionPrice = moneyValue(product?.action_price);
    if (!Number.isFinite(actionPrice) || actionPrice <= 0) return null;
    const oldCalc = averageCalc(calculateCost(costItem, catalog));
    const newCalc = averageCalc(
        calculateCost({ ...costItem, price: actionPrice }, catalog)
    );
    const oldProfit = oldCalc.profit;
    const newProfit = newCalc.profit;
    const oldMargin = oldCalc.margin;
    const newMargin = newCalc.margin;
    return {
        oldProfit,
        newProfit,
        profitDiff: newProfit - oldProfit,
        oldMargin,
        newMargin,
        marginLossPercent:
            oldProfit === 0 ? null : ((newProfit - oldProfit) / Math.abs(oldProfit)) * 100,
    };
};

export const productEconomics = (product, costItems, catalog) => {
    const offerId = product?.offer_id || product?.color?._id || '';
    const costItem = matchCostItem(costItems, offerId);
    if (!costItem) return null;
    const qty = Number(product.quantity) > 0 ? Number(product.quantity) : 1;
    const orderPrice = Number(product.price) || 0;
    const orderAvg = averageCalc(calculateCost({ ...costItem, price: orderPrice }, catalog));
    const tableAvg = averageCalc(calculateCost(costItem, catalog));
    return {
        orderProfit: orderAvg.profit * qty,
        orderMargin: orderAvg.margin,
        tableProfit: tableAvg.profit * qty,
        tableMargin: tableAvg.margin,
    };
};
